import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle, Scales, Tag, Warning } from '@phosphor-icons/react'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Badge,
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  KgInput,
  PhotoCapture,
  PickerSheet,
  Sheet,
  Stepper,
  useToast,
} from '@/ui'
import {
  kgWire,
  MISSING_REASONS,
  type MissingReason,
  type ReceiveShipmentPayload,
} from '../commands'
import type { BagView, StoreState, TripView } from '../projection'
import {
  checkWeigh,
  checkWeighPicks,
  classifyScan,
  isSameWeigher,
  receiveBlocks,
  reconcileManifest,
  type CheckWeighResult,
  type ReceiveScan,
} from '../rules'
import { Counter, ScanButton, YesNo } from '../components/bits'
import { useRefusalText } from '../components/useRefusalText'
import { StoreScreen } from '../components/StoreScreen'
import { useDraftState, useNow } from '../useStore'

interface WeightEntry {
  measurement_id: string
  value_g: number
  taken_at: string
  /** Out of tolerance and confirmed on a second entry → every bag of the lot. */
  confirmed_out: boolean
}

interface ReceiveDraft {
  step: number
  challan_ok: boolean
  scans: ReceiveScan[]
  /** container id → picked by (frozen when the trip is opened, so random picks never change). */
  picks: Record<string, 'SERVER' | 'PHONE'>
  weights: Record<string, WeightEntry>
  all_bags_lots: string[]
  vehicle_empty: boolean
  missing: Record<string, MissingReason>
  refused_photos: Record<string, string>
}

const emptyReceive = (): ReceiveDraft => ({
  step: 0,
  challan_ok: false,
  scans: [],
  picks: {},
  weights: {},
  all_bags_lots: [],
  vehicle_empty: false,
  missing: {},
  refused_photos: {},
})

/** Scans held while the manifest has not reached the phone (F5.2 offline). */
interface HeldDraft {
  vehicle_no: string
  scans: ReceiveScan[]
}

/** F5.2 — Receive at the godown against the trip manifest. */
export function ReceivePage() {
  const [tripId, setTripId] = useState<string | null>(null)
  const [held, setHeld] = useState(false)
  return (
    <StoreScreen title="store.receive">
      {(state) =>
        tripId && state.trips.get(tripId) ? (
          <ReceiveFlow
            state={state}
            trip={state.trips.get(tripId)!}
            onExit={() => setTripId(null)}
          />
        ) : held ? (
          <HeldScans state={state} onExit={() => setHeld(false)} />
        ) : (
          <TripPicker state={state} onPick={setTripId} onHeld={() => setHeld(true)} />
        )
      }
    </StoreScreen>
  )
}

function arrivingTrips(state: StoreState): TripView[] {
  return [...state.trips.values()].filter(
    (t) =>
      t.to_location_id === state.siteId &&
      t.status === 'IN_TRANSIT' &&
      t.local_status !== 'RECEIVED' &&
      t.local_status !== 'RECEIVED_SHORT',
  )
}

function TripPicker({
  state,
  onPick,
  onHeld,
}: {
  state: StoreState
  onPick: (id: string) => void
  onHeld: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const trips = arrivingTrips(state)
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-[17px] font-semibold">
        <BilingualLabel k="store.receive.arriving" />
      </h2>
      {trips.length === 0 && <Banner tone="info">{t('store.receive.noTrips')}</Banner>}
      {trips.map((trip) => (
        <button
          key={trip.shipment_id}
          type="button"
          onClick={() => onPick(trip.shipment_id)}
          className="flex min-h-touch-lg w-full flex-col gap-1 rounded-card border border-rule bg-surface px-4 py-3 text-left hover:border-ink/40"
        >
          <span className="flex items-center justify-between gap-2">
            <span className="font-mono text-[17px] font-bold">{trip.vehicle_no ?? '—'}</span>
            <Badge tone="info">{trip.code}</Badge>
          </span>
          <span className="text-[14px] text-muted">
            {t('store.receive.tripLine', {
              from: trip.from_name ?? '—',
              challan: trip.challan_no ?? '—',
              bags: trip.items.filter((i) => i.container_id).length,
            })}
          </span>
          {trip.dispatched_at && (
            <span className="text-[12px] text-muted">
              {t('store.receive.leftAt', { when: fmt.relative(trip.dispatched_at) })}
            </span>
          )}
        </button>
      ))}
      <Button variant="secondary" size="touch" fullWidth onClick={onHeld}>
        {t('store.receive.notListed')}
      </Button>
    </div>
  )
}

function HeldScans({ state, onExit }: { state: StoreState; onExit: () => void }) {
  const { t } = useI18n()
  const actor = useActor()
  const [draft, update, loaded] = useDraftState<HeldDraft>(
    `store.receive.held:${state.siteId}`,
    'store.receive.held',
    () => ({ vehicle_no: '', scans: [] }),
    actor.user_id,
  )
  if (!loaded) return null
  return (
    <div className="flex flex-col gap-4">
      <Banner tone="info" title={t('store.receive.heldTitle')}>
        {t('store.receive.heldBody')}
      </Banner>
      <Field label={<BilingualLabel k="store.load.vehicleNo" />}>
        <Input
          size="touch"
          value={draft.vehicle_no}
          className="font-mono uppercase"
          onChange={(e) => update((d) => ({ ...d, vehicle_no: e.target.value }))}
        />
      </Field>
      <Counter label={t('store.receive.heldCount')} value={draft.scans.length} />
      <ScanButton
        label={<BilingualLabel k="store.receive.scanBag" />}
        validate={(c) =>
          draft.scans.some((s) => s.qr_payload === c) ? t('store.refusal.DUPLICATE') : null
        }
        onScan={(c) =>
          update((d) =>
            d.scans.some((s) => s.qr_payload === c)
              ? d
              : {
                  ...d,
                  scans: [...d.scans, { qr_payload: c, scanned_at: new Date().toISOString() }],
                },
          )
        }
      />
      <Button variant="secondary" size="touch" onClick={onExit}>
        {t('common.back')}
      </Button>
    </div>
  )
}

function ReceiveFlow({
  state,
  trip,
  onExit,
}: {
  state: StoreState
  trip: TripView
  onExit: () => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const navigate = useNavigate()
  const actor = useActor()
  const refusal = useRefusalText()
  const now = useNow()
  const [draft, update, loaded, clear] = useDraftState<ReceiveDraft>(
    `store.receive:${trip.shipment_id}`,
    'store.receive',
    emptyReceive,
    actor.user_id,
  )
  const [heldDraft, , heldLoaded, clearHeld] = useDraftState<HeldDraft>(
    `store.receive.held:${state.siteId}`,
    'store.receive.held',
    () => ({ vehicle_no: '', scans: [] }),
    actor.user_id,
  )
  const [saving, setSaving] = useState(false)
  const [weighing, setWeighing] = useState<BagView | null>(null)
  const [untagged, setUntagged] = useState(false)

  const missingSet = useMemo(() => new Set(Object.keys(draft.missing)), [draft.missing])
  const rec = useMemo(
    () => reconcileManifest(state, trip, draft.scans, missingSet),
    [state, trip, draft.scans, missingSet],
  )
  const allBagsLots = useMemo(() => new Set(draft.all_bags_lots), [draft.all_bags_lots])

  // Effective picks: frozen picks + every bag of a lot gone out of tolerance + extras.
  const picks = useMemo(() => {
    const m = new Map(Object.entries(draft.picks))
    for (const i of trip.items)
      if (i.container_id && i.unit_id && allBagsLots.has(i.unit_id) && !m.has(i.container_id))
        m.set(i.container_id, 'PHONE')
    for (const x of rec.extras) if (!m.has(x.bag.container_id)) m.set(x.bag.container_id, 'PHONE')
    return m
  }, [draft.picks, trip.items, allBagsLots, rec.extras])

  const scannedBags: BagView[] = useMemo(
    () => [
      ...rec.unloaded
        .map((u) => (u.item.container_id ? state.bags.get(u.item.container_id) : undefined))
        .filter((b): b is BagView => !!b),
      ...rec.extras.map((x) => x.bag),
    ],
    [rec, state.bags],
  )
  const toWeigh = scannedBags.filter((b) => picks.has(b.container_id))
  const weightsMissing = toWeigh.filter((b) => !draft.weights[b.container_id]).length
  const blocks = receiveBlocks({
    hasManifest: true,
    vehicleEmpty: draft.vehicle_empty,
    openItems: rec.open.length,
    weightsMissing,
  })

  const start = () =>
    update((d) => {
      if (Object.keys(d.picks).length) return { ...d, step: 1 }
      const p = checkWeighPicks({
        state,
        trip,
        extras: [],
        perLot: state.cache.settings.checks_per_lot,
        allBagsLots: new Set(),
      })
      return { ...d, step: 1, picks: Object.fromEntries(p) }
    })

  const addScan = (code: string) =>
    update((d) =>
      d.scans.some((s) => s.qr_payload === code)
        ? d
        : { ...d, scans: [...d.scans, { qr_payload: code, scanned_at: new Date().toISOString() }] },
    )

  const lotShort = rec.open.length + rec.missing.length > 0

  const receive = async () => {
    if (blocks.length) return
    setSaving(true)
    try {
      const scale = state.cache.scales[0]?.id ?? null
      const payload: ReceiveShipmentPayload = {
        shipment_id: trip.shipment_id,
        received_at: new Date().toISOString(),
        unloads: rec.unloaded.map((u) => ({ item_id: u.item.item_id, unloaded_at: u.scanned_at })),
        extras: rec.extras.map((x) => ({
          item_id: newId(),
          qr_payload: x.bag.qr_payload,
          unloaded_at: x.scanned_at,
        })),
        check_weights: toWeigh.map((b) => {
          const w = draft.weights[b.container_id]!
          return {
            measurement_id: w.measurement_id,
            container_id: b.container_id,
            value: kgWire(w.value_g),
            value_g: w.value_g,
            uom: 'KG' as const,
            method: 'MANUAL_ENTRY' as const,
            equipment_id: scale,
            taken_at: w.taken_at,
            picked_by: picks.get(b.container_id) ?? 'PHONE',
          }
        }),
        missing: rec.missing.map((i) => ({
          item_id: i.item_id,
          reason_code: draft.missing[i.item_id]!,
        })),
        refused_scans: rec.refused.map((r) => ({
          ...r,
          photo_id: draft.refused_photos[r.qr_payload] ?? null,
        })),
      }
      const mediaIds = [
        ...new Set(payload.refused_scans.map((r) => r.photo_id).filter((x): x is string => !!x)),
      ]
      const parents = trip.local
        ? [...new Set(scannedBags.map((b) => b.pending_command_id).filter((x): x is string => !!x))]
        : []
      await enqueueCommand({
        type: 'receive_shipment',
        payload,
        actorId: actor.user_id,
        mediaIds,
        parentIds: parents,
      })
      await clear()
      toast.success(
        t(rec.missing.length ? 'store.receive.doneShort' : 'store.receive.done', {
          code: trip.code,
        }),
      )
      navigate('/m/store/put-away')
    } catch (e) {
      toast.error(t('store.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (!loaded || !heldLoaded) return null
  const steps = [
    { id: 'trip', label: t('store.receive.stepTrip') },
    { id: 'scan', label: t('store.receive.stepScan') },
    { id: 'weigh', label: t('store.receive.stepWeigh') },
    { id: 'close', label: t('store.receive.stepClose') },
  ]
  const go = (step: number) => update((d) => ({ ...d, step }))

  return (
    <div className="flex flex-col gap-4">
      <Stepper steps={steps} current={draft.step} />
      <Card>
        <CardBody className="flex items-center justify-between gap-2 py-3">
          <div>
            <div className="font-mono text-[17px] font-bold">{trip.vehicle_no}</div>
            <div className="text-[13px] text-muted">
              {trip.code} · {trip.from_name}
            </div>
          </div>
          <Counter
            label={t('store.receive.trip')}
            value={rec.bagsScanned}
            total={rec.bagsExpected}
          />
        </CardBody>
      </Card>

      {draft.step === 0 && (
        <Card>
          <CardBody className="flex flex-col gap-4">
            <Field label={<BilingualLabel k="store.receive.challanCheck" />}>
              <div className="rounded-chip bg-paper-tint px-3 py-3 text-center font-mono text-[22px] font-bold">
                {trip.challan_no ?? '—'}
              </div>
            </Field>
            <YesNo
              value={draft.challan_ok ? true : null}
              onChange={(v) => update((d) => ({ ...d, challan_ok: v }))}
            />
            {heldDraft.scans.length > 0 && (
              <Button
                variant="secondary"
                size="touch"
                onClick={() => {
                  update((d) => ({
                    ...d,
                    scans: [
                      ...d.scans,
                      ...heldDraft.scans.filter(
                        (h) => !d.scans.some((s) => s.qr_payload === h.qr_payload),
                      ),
                    ],
                  }))
                  void clearHeld()
                }}
              >
                {t('store.receive.useHeld', { count: heldDraft.scans.length })}
              </Button>
            )}
            <div className="flex gap-2">
              <Button variant="secondary" size="xl" onClick={onExit}>
                {t('common.back')}
              </Button>
              <Button size="xl" fullWidth disabled={!draft.challan_ok} onClick={start}>
                {t('common.continue')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {draft.step === 1 && (
        <>
          <ScanButton
            label={<BilingualLabel k="store.receive.scanBag" />}
            validate={(c) => {
              if (draft.scans.some((s) => s.qr_payload === c)) return refusal('DUPLICATE')
              if (trip.items.some((i) => i.qr_payload.toUpperCase() === c)) return null
              // Not on the list: a good bag tag is an extra (received); anything else is refused
              // with a beep but kept, so it is reported in refused_scans with its photo.
              const k = classifyScan(state, c)
              if (k.kind === 'BAG') return null
              addScan(c)
              return refusal(
                k.kind === 'DEAD'
                  ? k.status === 'VOID'
                    ? 'TAG_VOID'
                    : 'TAG_RETIRED'
                  : k.kind === 'UNKNOWN'
                    ? 'TAG_UNKNOWN'
                    : 'TAG_WRONG_KIND',
              )
            }}
            onScan={addScan}
          />
          <Card>
            <CardHeader eyebrow={t('store.lots')} />
            <CardBody className="py-2">
              <ul>
                {[...rec.byLot.entries()].map(([unit, v]) => (
                  <li
                    key={unit}
                    className="flex items-center justify-between border-b border-rule/70 py-2 last:border-b-0"
                  >
                    <span className="font-mono text-[14px]">
                      {state.lots.get(unit)?.code ?? unit}
                    </span>
                    <span className={v.scanned === v.expected ? 'font-mono text-ok' : 'font-mono'}>
                      {v.scanned} / {v.expected}
                    </span>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
          {rec.extras.length > 0 && (
            <Banner
              tone="warn"
              title={t('store.receive.extrasTitle', { count: rec.extras.length })}
            >
              {rec.extras.map((x) => x.bag.qr_payload).join(', ')} · {t('store.receive.extrasBody')}
            </Banner>
          )}
          {rec.refused.length > 0 && (
            <Card>
              <CardHeader
                eyebrow={<BilingualLabel k="store.receive.problemBags" />}
                description={t('store.receive.problemBody')}
              />
              <CardBody className="flex flex-col gap-3">
                {rec.refused.map((r) => (
                  <div
                    key={r.qr_payload}
                    className="flex flex-col gap-2 border-b border-rule/70 pb-3 last:border-b-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono">{r.qr_payload}</span>
                      <Badge tone="danger">{refusal(r.reason)}</Badge>
                    </div>
                    <PhotoCapture
                      value={draft.refused_photos[r.qr_payload] ?? null}
                      onChange={(id) =>
                        update((d) => {
                          const photos = { ...d.refused_photos }
                          if (id) photos[r.qr_payload] = id
                          else delete photos[r.qr_payload]
                          return { ...d, refused_photos: photos }
                        })
                      }
                    />
                  </div>
                ))}
              </CardBody>
            </Card>
          )}
          <Button
            variant="ghost"
            size="touch"
            leadingIcon={<Tag aria-hidden="true" />}
            onClick={() => setUntagged(true)}
          >
            <BilingualLabel k="store.receive.untagged" />
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="xl" onClick={() => go(0)}>
              {t('common.back')}
            </Button>
            <Button size="xl" fullWidth onClick={() => go(2)}>
              {t('common.continue')}
            </Button>
          </div>
        </>
      )}

      {draft.step === 2 && (
        <>
          <Banner tone="info" title={t('store.receive.weighTitle', { count: toWeigh.length })}>
            {t('store.receive.weighBody')}
          </Banner>
          <Card>
            <CardBody className="py-1">
              <ul>
                {toWeigh.map((b) => {
                  const w = draft.weights[b.container_id]
                  const lot = state.lots.get(b.unit_id)
                  return (
                    <li
                      key={b.container_id}
                      className="flex items-center gap-3 border-b border-rule/70 py-2.5 last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[15px]">{b.qr_payload}</div>
                        <div className="text-[12px] text-muted">
                          {lot?.code}
                          {lot?.capture_mode === 'PAPER' && ` · ${t('store.receive.paperDay')}`}
                        </div>
                      </div>
                      {w ? (
                        <button
                          type="button"
                          onClick={() => setWeighing(b)}
                          className="flex items-center gap-1 font-mono text-[15px]"
                        >
                          {w.confirmed_out ? (
                            <Warning className="text-warn" aria-hidden="true" />
                          ) : (
                            <CheckCircle className="text-ok" aria-hidden="true" />
                          )}
                          {fmt.weight(w.value_g)}
                        </button>
                      ) : (
                        <Button
                          size="touch"
                          variant="secondary"
                          leadingIcon={<Scales aria-hidden="true" />}
                          onClick={() => setWeighing(b)}
                        >
                          {t('store.receive.weigh')}
                        </Button>
                      )}
                    </li>
                  )
                })}
                {toWeigh.length === 0 && (
                  <li className="py-4 text-center text-muted">
                    {t('store.receive.nothingToWeigh')}
                  </li>
                )}
              </ul>
            </CardBody>
          </Card>
          <div className="flex gap-2">
            <Button variant="secondary" size="xl" onClick={() => go(1)}>
              {t('common.back')}
            </Button>
            <Button size="xl" fullWidth onClick={() => go(3)}>
              {t('common.continue')}
            </Button>
          </div>
        </>
      )}

      {draft.step === 3 && (
        <>
          <Card>
            <CardBody className="flex flex-col gap-3">
              <BilingualLabel
                k="store.receive.vehicleEmpty"
                className="text-[17px] font-semibold"
              />
              <YesNo
                value={draft.vehicle_empty ? true : null}
                onChange={(v) =>
                  update((d) => ({ ...d, vehicle_empty: v, missing: v ? d.missing : {} }))
                }
              />
            </CardBody>
          </Card>
          {(rec.open.length > 0 || rec.missing.length > 0) && (
            <Card>
              <CardHeader
                eyebrow={t('store.receive.notScanned', {
                  count: rec.open.length + rec.missing.length,
                })}
                description={
                  draft.vehicle_empty
                    ? t('store.receive.missingHint')
                    : t('store.receive.emptyFirst')
                }
              />
              <CardBody className="py-1">
                <ul>
                  {[...rec.open, ...rec.missing].map((i) => {
                    const reason = draft.missing[i.item_id]
                    return (
                      <li
                        key={i.item_id}
                        className="flex flex-col gap-2 border-b border-rule/70 py-2.5 last:border-b-0"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-mono">{i.qr_payload}</div>
                            <div className="text-[12px] text-muted">
                              {i.unit_id ? state.lots.get(i.unit_id)?.code : ''}
                            </div>
                          </div>
                          <Button
                            size="touch"
                            variant={reason ? 'danger' : 'secondary'}
                            disabled={!draft.vehicle_empty}
                            onClick={() =>
                              update((d) => {
                                const m = { ...d.missing }
                                if (m[i.item_id]) delete m[i.item_id]
                                else m[i.item_id] = 'NOT_ON_VEHICLE'
                                return { ...d, missing: m }
                              })
                            }
                          >
                            <BilingualLabel k="store.receive.missing" />
                          </Button>
                        </div>
                        {reason && (
                          <PickerSheet
                            title={t('store.receive.missingReason')}
                            value={reason}
                            options={MISSING_REASONS.map((r) => ({
                              value: r,
                              label: t(`store.missingReason.${r}` as const),
                            }))}
                            onChange={(v) =>
                              update((d) => ({ ...d, missing: { ...d.missing, [i.item_id]: v } }))
                            }
                          />
                        )}
                      </li>
                    )
                  })}
                </ul>
              </CardBody>
            </Card>
          )}
          {blocks.length > 0 && (
            <Banner tone="warn" title={t('store.receive.lockedTitle')}>
              <ul className="list-inside list-disc">
                {blocks.map((b) => (
                  <li key={b}>
                    {t(`store.receive.block.${b}` as const, {
                      open: rec.open.length,
                      weights: weightsMissing,
                    })}
                  </li>
                ))}
              </ul>
            </Banner>
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="xl" onClick={() => go(2)}>
              {t('common.back')}
            </Button>
            <Button
              size="xl"
              variant="accent"
              fullWidth
              disabled={blocks.length > 0}
              loading={saving}
              onClick={() => void receive()}
            >
              <BilingualLabel k="store.receive.receive" />
            </Button>
          </div>
        </>
      )}

      {weighing && (
        <WeighSheet
          state={state}
          bag={weighing}
          actorId={actor.user_id}
          now={now}
          existing={draft.weights[weighing.container_id] ?? null}
          onClose={() => setWeighing(null)}
          onSave={(entry) => {
            update((d) => ({
              ...d,
              weights: { ...d.weights, [weighing.container_id]: entry },
              all_bags_lots:
                entry.confirmed_out && !d.all_bags_lots.includes(weighing.unit_id)
                  ? [...d.all_bags_lots, weighing.unit_id]
                  : d.all_bags_lots,
            }))
            setWeighing(null)
          }}
        />
      )}

      <Sheet
        open={untagged}
        onClose={() => setUntagged(false)}
        title={<BilingualLabel k="store.receive.untagged" />}
      >
        <div className="flex flex-col gap-3">
          {lotShort ? (
            <>
              <Banner tone="warn">{t('store.receive.untaggedShort')}</Banner>
              <Link
                to={`/m/store/replace?trip=${encodeURIComponent(trip.shipment_id)}`}
                className="inline-flex min-h-touch-lg items-center justify-center rounded-chip border border-ink bg-ink px-6 font-mono text-[14px] font-bold uppercase tracking-label text-paper"
              >
                {t('store.replace.title')}
              </Link>
            </>
          ) : (
            <Banner tone="info">{t('store.receive.untaggedNoShort')}</Banner>
          )}
        </div>
      </Sheet>
    </div>
  )
}

function WeighSheet({
  state,
  bag,
  actorId,
  now,
  existing,
  onClose,
  onSave,
}: {
  state: StoreState
  bag: BagView
  actorId: string
  now: number
  existing: WeightEntry | null
  onClose: () => void
  onSave: (e: WeightEntry) => void
}) {
  const { t } = useI18n()
  const fmt = useFormat()
  const [value, setValue] = useState<number | null>(existing?.value_g ?? null)
  const [first, setFirst] = useState<CheckWeighResult | null>(null)
  const [firstValue, setFirstValue] = useState<number | null>(null)
  const lot = state.lots.get(bag.unit_id)
  const sameWeigher = isSameWeigher(bag, actorId)

  const evaluate = (g: number) =>
    checkWeigh({
      valueG: g,
      tareG: bag.tare_g,
      netGateG: bag.net_g_gate ?? 0,
      cropId: lot?.crop_id,
      committedAt: lot?.committed_at,
      takenAt: now,
      settings: state.cache.settings,
    })

  const submit = () => {
    if (value === null || value <= 0 || sameWeigher) return
    const r = evaluate(value)
    const entry = (confirmedOut: boolean): WeightEntry => ({
      measurement_id: existing?.measurement_id ?? newId(),
      value_g: value,
      taken_at: new Date().toISOString(),
      confirmed_out: confirmedOut,
    })
    if (r.within) return onSave(entry(false))
    if (!first) {
      // Out of tolerance: ask for the weight again (F5.2).
      setFirst(r)
      setFirstValue(value)
      setValue(null)
      return
    }
    onSave(entry(true))
  }

  const shown = value !== null && value > 0 ? evaluate(value) : null

  return (
    <Sheet
      open
      onClose={onClose}
      title={<BilingualLabel k="store.receive.checkWeight" />}
      description={`${bag.qr_payload} · ${lot?.code ?? ''}`}
      footer={
        <Button
          size="xl"
          fullWidth
          disabled={value === null || value <= 0 || sameWeigher}
          onClick={submit}
        >
          {first ? t('store.receive.confirmSecond') : t('common.save')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {sameWeigher && (
          <Banner tone="error" title={t('store.receive.sameWeigherTitle')}>
            {t('store.receive.sameWeigherBody')}
          </Banner>
        )}
        {first && (
          <Banner tone="warn" title={t('store.receive.outOfTolTitle')}>
            {t('store.receive.outOfTolBody', { first: fmt.weight(firstValue ?? 0) })}
          </Banner>
        )}
        <Field label={<BilingualLabel k="store.receive.checkWeight" />} required>
          <KgInput value={value} onChange={setValue} disabled={sameWeigher} data-autofocus />
        </Field>
        {state.cache.scales[0] && (
          <p className="text-[13px] text-muted">
            {t('store.receive.scale', { name: state.cache.scales[0].name })}
          </p>
        )}
        {shown && (
          <div className="rounded-card border border-rule bg-paper-tint px-3 py-2 text-[14px]">
            <div>{t('store.receive.gateNet', { kg: fmt.weight(bag.net_g_gate ?? 0) })}</div>
            <div>
              {t('store.receive.expectedNet', {
                kg: fmt.weight(shown.expectedNet),
                drying: fmt.weight(shown.drying),
              })}
            </div>
            <div>{t('store.receive.receiveNet', { kg: fmt.weight(shown.receiveNet) })}</div>
            <div className={shown.within ? 'font-semibold text-ok' : 'font-semibold text-warn'}>
              {t(shown.within ? 'store.receive.within' : 'store.receive.outside', {
                diff: fmt.weight(shown.diff),
                tol: fmt.weight(shown.tolerance),
              })}
            </div>
          </div>
        )}
      </div>
    </Sheet>
  )
}
